import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Send } from "lucide-react";
import Swal from "sweetalert2";
import { addEmailToCartMailChimp, apiMail } from "../../actions/userActions";
import { consultation } from "../../emails";
import { ContactResponse } from "../../emails/new";
import ReCAPTCHA from "react-google-recaptcha";

const ContactForm = ({ landing = false, home = false }) => {
	const navigate = useNavigate();
	const [form, setForm] = useState({
		fName: "",
		lName: "",
		email: "",
		phone: "",
		message: "",
		inquiry: "",
		hpField: "",
	});
	const [captchaToken, setCaptchaToken] = useState(null);

	// Animation constants matching home page
	const bounceTransition = {
		duration: 0.4,
		ease: [0.68, -0.25, 0.265, 1.25],
	};

	const inputVariants = {
		initial: { opacity: 0, y: 20 },
		animate: { opacity: 1, y: 0 },
	};

	const inputClasses = `w-full px-4 py-3 bg-white rounded-lg border border-gray-200 
    focus:ring-2 focus:ring-primary/20 focus:border-primary 
    placeholder:text-gray-400 text-gray-900
    transition-all duration-200 ease-in-out`;

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (form.hpField) {
			console.log("Bot detected via honeypot field!");
			return;
		}

		if (!captchaToken) {
			alert("Please verify you're not a robot.");
			return;
		}

		try {
			await addEmailToCartMailChimp(
				form.email,
				"Contact Form",
				form.fName,
				form.lName
			);
			await apiMail(
				"info@offcutshades.com",
				`Contact request: ${form.fName} ${form.lName}`,
				consultation(form),
				captchaToken
			);
			await apiMail(
				form.email,
				`Thank You For Contacting Us: ${form.fName} ${form.lName}`,
				<ContactResponse name={`${form.fName} ${form.lName}`} />
			);

			if (landing) {
				setForm({
					fName: "",
					lName: "",
					email: "",
					phone: "",
					message: "",
					inquiry: "",
				});

				await Swal.fire({
					icon: "success",
					title: "Success",
					text: "Thank you for contacting us! We've received your submission and will be in touch shortly to schedule an appointment",
				});

				navigate("/");
			} else {
				await Swal.fire({
					icon: "success",
					title: "Success",
					text: "Thank you for contacting us! We've received your submission and will be in touch shortly.",
				});
			}
		} catch (err) {
			console.error(err);
			await Swal.fire({
				icon: "error",
				title: "Error",
				text: "Unfortunately, we encountered an error. Please try again later.",
			});
		}
	};

	return (
		<form
			onSubmit={handleSubmit}
			className={
				landing ? "w-full max-w-md mx-auto space-y-4" : "w-full space-y-6"
			}
		>
			{/* {!landing && (
				<motion.h2
					className="text-2xl font-bold text-gray-900 mb-6"
					initial={{ opacity: 0, y: -20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={bounceTransition}
				>
					Send us a message
				</motion.h2>
			)} */}

			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				<motion.div
					variants={inputVariants}
					initial="initial"
					animate="animate"
					transition={{ ...bounceTransition, delay: 0.1 }}
				>
					<input
						type="text"
						name="fname"
						placeholder="First Name"
						required
						className={inputClasses}
						value={form.fName}
						onChange={(e) =>
							setForm((prev) => ({ ...prev, fName: e.target.value }))
						}
					/>
				</motion.div>

				<motion.div
					variants={inputVariants}
					initial="initial"
					animate="animate"
					transition={{ ...bounceTransition, delay: 0.2 }}
				>
					<input
						type="text"
						name="lname"
						placeholder="Last Name"
						required
						className={inputClasses}
						value={form.lName}
						onChange={(e) =>
							setForm((prev) => ({ ...prev, lName: e.target.value }))
						}
					/>
				</motion.div>
			</div>

			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				<motion.div
					variants={inputVariants}
					initial="initial"
					animate="animate"
					transition={{ ...bounceTransition, delay: 0.3 }}
				>
					<input
						type="email"
						name="email"
						placeholder="Email Address"
						required
						className={inputClasses}
						value={form.email}
						onChange={(e) =>
							setForm((prev) => ({ ...prev, email: e.target.value }))
						}
					/>
				</motion.div>

				<motion.div
					variants={inputVariants}
					initial="initial"
					animate="animate"
					transition={{ ...bounceTransition, delay: 0.4 }}
				>
					<input
						type="tel"
						name="phone"
						placeholder="Phone Number"
						required
						className={inputClasses}
						value={form.phone}
						onChange={(e) =>
							setForm((prev) => ({ ...prev, phone: e.target.value }))
						}
					/>
				</motion.div>
			</div>

			{/* Type of inquiry */}
			<motion.div
				variants={inputVariants}
				initial="initial"
				animate="animate"
				exit="initial"
				transition={{ ...bounceTransition, delay: 0.5 }}
			>
				<select
					name="inquiry"
					className={`${inputClasses} appearance-none`}
					value={form.inquiry}
					onChange={(e) =>
						setForm((prev) => ({ ...prev, inquiry: e.target.value }))
					}
				>
					<option disabled value="">
						Type of Inquiry
					</option>
					<option value="consultation">Consultation</option>
					<option value="order">Order Inquiry</option>
					<option value="website">Website Inquiry</option>
					<option value="feedback">Feedback</option>
					<option value="quote">Quote</option>
					<option value="other">Other</option>
				</select>
			</motion.div>

			<motion.div
				variants={inputVariants}
				initial="initial"
				animate="animate"
				transition={{ ...bounceTransition, delay: 0.5 }}
			>
				<textarea
					name="message"
					placeholder="A brief description of your inquriy"
					required
					rows={5}
					className={`${inputClasses} resize-none`}
					value={form.message}
					onChange={(e) =>
						setForm((prev) => ({ ...prev, message: e.target.value }))
					}
				/>
				<input
					type="text"
					name="hpField"
					value={form.hpField}
					onChange={(e) =>
						setForm((prev) => ({ ...prev, hpField: e.target.value }))
					}
					style={{ display: "none" }}
					autoComplete="off"
				/>
			</motion.div>

			<motion.div
				className="flex justify-center"
				variants={inputVariants}
				initial="initial"
				animate="animate"
				transition={{ ...bounceTransition, delay: 0.6 }}
			>
				<ReCAPTCHA
					sitekey="6LcbcyUsAAAAANIFkXj-1DbMqQJqsEHgXF9dRZxo"
					onChange={(token) => setCaptchaToken(token)}
				/>
				<motion.button
					type="submit"
					className={`
            inline-flex items-center justify-center gap-2 px-8 py-4 
            ${
							home
								? "bg-primary hover:bg-secondary"
								: "bg-secondary hover:bg-primary"
						}
            text-white font-semibold rounded-xl shadow-lg
            transition-colors duration-200
          `}
					whileHover={{ scale: 1.05 }}
					whileTap={{ scale: 0.95 }}
				>
					<span>Submit</span>
					<Send size={20} />
				</motion.button>
			</motion.div>
		</form>
	);
};

export default ContactForm;
